'use client';
import {useCallback, useEffect, useRef, useState} from 'react';

const MIN_SCROLL_STEP_PX = 160;
const SCROLL_STEP_VIEWPORT_FRACTION = 0.6;

export interface DirectionalScrollerOptions {
  fullPageStep?: boolean;
}

export function useDirectionalScroller<T extends HTMLElement>(itemCount?: number, options: DirectionalScrollerOptions = {}) {
  const {fullPageStep = false} = options;
  const ref = useRef<T>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // `scrollLeft` is negative in RTL on most engines — comparing against the
  // magnitude keeps the arrow-disabled logic direction-agnostic.
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft);
    setCanPrev(pos > 1);
    setCanNext(pos < max - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    update();
    el.addEventListener('scroll', update, {passive: true});
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [update, itemCount]);

  const scrollByDir = useCallback(
    (dir: -1 | 1) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const rtl = getComputedStyle(el).direction === 'rtl';
      const step = fullPageStep ? el.clientWidth : Math.max(MIN_SCROLL_STEP_PX, el.clientWidth * SCROLL_STEP_VIEWPORT_FRACTION);
      el.scrollBy({left: dir * (rtl ? -1 : 1) * step, behavior: 'smooth'});
    },
    [fullPageStep],
  );

  return {ref, canPrev, canNext, scrollByDir};
}
